/**
 * The dynamic-group rule builder's field → operator matrix — ONE source of
 * truth (GR5, audit 2026-09-25).
 *
 * `components/recipients/groups/dynamic-rule-builder.tsx` renders exactly the
 * fields and operators this module lists, and
 * `apps/api/tests/unit/recipients/group-rule-builder-pairs.postgres-cases.ts`
 * requires this file and runs EVERY pair it can offer through the real engine
 * on PostgreSQL: a pair the engine refuses (400) or cannot execute (500) fails
 * that guard. The builder used to hold its own copy of this logic and offered
 * pairs that crashed ("Status contains" on an enum), never matched, or were
 * refused ("Baseline is any of").
 *
 * Pure data and functions only: no React and no `@/` imports, so the API test
 * can `require` it.
 */
import type { FieldOperator, FieldType } from '../types/recipients'

/** A field as the builder offers it. `options` are attached at runtime. */
export interface RuleFieldSpec {
  key: string
  label: string
  type: FieldType
  group: 'profile' | 'custom' | 'relationship' | 'behavioral' | 'engagement' | 'phishtrainer'
  options?: { value: string; label: string }[]
  /**
   * Rendered as a Select when the workspace has distinct values for it, as
   * free text when it has none — both shapes reach the engine.
   */
  selectWhenOptions?: boolean
  /** Behavioral catalog fields carry the operators the BE catalog declares. */
  catalogOperators?: FieldOperator[]
}

/** Customer-facing label for every operator. */
export const OPERATOR_LABELS: Record<FieldOperator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  greater_than: 'greater than',
  less_than: 'less than',
  between: 'between',
  before_date: 'is before',
  after_date: 'is after',
  in_last_days: 'in last X days',
  in_list: 'is any of',
  not_in_list: 'is none of',
}

/** Tag / group membership operators, with their relationship wording. */
export const RELATIONSHIP_OPERATORS: { value: FieldOperator; label: string }[] = [
  { value: 'is', label: 'has tag / is in group' },
  { value: 'is_not', label: 'does not have / is not in' },
  { value: 'in_list', label: 'has any of' },
  { value: 'not_in_list', label: 'has none of' },
  { value: 'is_empty', label: 'has none' },
  { value: 'is_not_empty', label: 'has at least one' },
]

/** Operators offered for a value type. */
export function operatorsForType(type: FieldType): FieldOperator[] {
  const common: FieldOperator[] = ['is', 'is_not']
  switch (type) {
    case 'Text':
    case 'Email':
    case 'URL':
    case 'Phone':
      return [
        ...common,
        'in_list',
        'not_in_list',
        'contains',
        'not_contains',
        'starts_with',
        'ends_with',
        'is_empty',
        'is_not_empty',
      ]
    case 'Select':
    case 'Multi Select':
      return [...common, 'in_list', 'not_in_list', 'contains', 'not_contains']
    case 'Number':
    case 'Currency':
    case 'Percentage':
      return [...common, 'greater_than', 'less_than', 'between']
    case 'Date':
      return ['before_date', 'after_date', 'in_last_days']
    case 'Boolean':
      return common
    case 'Recipient Lookup':
      return [...common, 'in_list', 'not_in_list']
    default:
      return common
  }
}

/**
 * Fields whose backing cannot take every operator of their type. Baseline is
 * two states — "contains" means nothing there — and the engine evaluates
 * is / is not / is any of / is none of (GR5).
 */
export const FIELD_OPERATORS: Readonly<Record<string, FieldOperator[]>> = {
  pt_baseline: ['is', 'is_not', 'in_list', 'not_in_list'],
}

/** The operators the builder offers for a field. */
export function operatorsForField(field: RuleFieldSpec | undefined): FieldOperator[] {
  if (!field) return []
  if (field.group === 'behavioral') return field.catalogOperators ?? []
  if (field.group === 'relationship') return RELATIONSHIP_OPERATORS.map((o) => o.value)
  return FIELD_OPERATORS[field.key] ?? operatorsForType(field.type)
}

/**
 * The risk_amplifier_level enum's labels, in the database's order. Risk
 * Amplifier was offered as a Number, so "greater than 2" compared an enum with
 * a number (22P02) and "is 2" matched nobody. The API guard checks this list
 * against the pgEnum.
 */
export const RISK_AMPLIFIER_OPTIONS: { value: string; label: string }[] = [
  'None',
  'Very Low',
  'Low',
  'Medium',
  'High',
  'Very High',
  'Critical',
].map((v) => ({ value: v, label: v }))

/**
 * The builder's own fields (the behavioral catalog comes from the API, custom
 * fields from the workspace). Keys MUST match the group engine's
 * resolveFieldToColumn (rule-to-sql STANDARD_FIELD_MAP + specials).
 */
export const BUILDER_FIELDS: readonly RuleFieldSpec[] = [
  // Identity
  { key: 'firstName', label: 'First Name', type: 'Text', group: 'profile' },
  { key: 'lastName', label: 'Last Name', type: 'Text', group: 'profile' },
  { key: 'displayName', label: 'Display Name', type: 'Text', group: 'profile' },
  { key: 'email', label: 'Email', type: 'Email', group: 'profile' },
  { key: 'phoneNumber', label: 'Phone Number', type: 'Text', group: 'profile' },
  { key: 'domainName', label: 'Domain', type: 'Text', group: 'profile' },
  // Org & access
  { key: 'status', label: 'Status', type: 'Select', group: 'profile' },
  { key: 'department', label: 'Department', type: 'Select', group: 'profile' },
  { key: 'designation', label: 'Designation', type: 'Select', group: 'profile' },
  { key: 'manager_email', label: 'Manager (email)', type: 'Text', group: 'profile' },
  { key: 'employment_type', label: 'Employment Type', type: 'Select', group: 'profile' },
  { key: 'is_remote', label: 'Remote Worker', type: 'Boolean', group: 'profile' },
  { key: 'sourceType', label: 'Source', type: 'Select', group: 'profile' },
  // Location
  { key: 'location', label: 'Location', type: 'Select', group: 'profile' },
  { key: 'country', label: 'Country', type: 'Select', group: 'profile' },
  { key: 'city', label: 'City', type: 'Text', group: 'profile' },
  { key: 'state', label: 'State / Region', type: 'Text', group: 'profile' },
  { key: 'postalCode', label: 'Postal Code', type: 'Text', group: 'profile' },
  // A person's zone and language: named as the recipient form and page name them.
  { key: 'timezone', label: 'Time Zone', type: 'Text', group: 'profile' },
  { key: 'locale', label: 'Language', type: 'Text', group: 'profile' },
  // Engagement, gamification & lifecycle dates
  { key: 'totalPoints', label: 'Total Points', type: 'Number', group: 'engagement' },
  { key: 'currentStreak', label: 'Current Streak', type: 'Number', group: 'engagement' },
  { key: 'longestStreak', label: 'Longest Streak', type: 'Number', group: 'engagement' },
  { key: 'profileCompleteness', label: 'Profile Completeness', type: 'Percentage', group: 'engagement' },
  {
    key: 'riskAmplifier',
    label: 'Risk Amplifier',
    type: 'Select',
    group: 'engagement',
    options: RISK_AMPLIFIER_OPTIONS,
  },
  { key: 'dateOfBirth', label: 'Date of Birth', type: 'Date', group: 'engagement' },
  { key: 'lastActivityDate', label: 'Last Activity', type: 'Date', group: 'engagement' },
  { key: 'createdAt', label: 'Created Date', type: 'Date', group: 'engagement' },
  { key: 'updatedAt', label: 'Updated Date', type: 'Date', group: 'engagement' },
  // PhishTrainer per-recipient state (phishtrainer_users)
  {
    key: 'pt_baseline',
    label: 'Baseline',
    type: 'Select',
    group: 'phishtrainer',
    options: [
      { value: 'Completed', label: 'Completed' },
      { value: 'Not completed', label: 'Not completed' },
    ],
  },
  { key: 'pt_risk_band', label: 'Risk Band', type: 'Text', group: 'phishtrainer', selectWhenOptions: true },
  { key: 'pt_seniority', label: 'Seniority Level', type: 'Text', group: 'phishtrainer', selectWhenOptions: true },
  { key: 'pt_status', label: 'PhishTrainer Status', type: 'Text', group: 'phishtrainer', selectWhenOptions: true },
  { key: 'pt_total_rounds', label: 'Total Rounds', type: 'Number', group: 'phishtrainer' },
  { key: 'pt_points', label: 'PhishTrainer Points', type: 'Number', group: 'phishtrainer' },
  { key: 'pt_last_round', label: 'Last Round', type: 'Date', group: 'phishtrainer' },
  // Tags & groups
  { key: 'tag', label: 'Tag', type: 'Select', group: 'relationship' },
  { key: 'group', label: 'Group Membership', type: 'Select', group: 'relationship' },
  // "Is in any group of category X" (vCRO lane B2); options are the workspace's
  // group categories. The API engine resolves it (rule-to-sql groupCategory).
  { key: 'groupCategory', label: 'Group Category', type: 'Select', group: 'relationship' },
]

/** Every value type operatorsForType understands. */
export const KNOWN_FIELD_TYPES: readonly FieldType[] = [
  'Text',
  'Number',
  'Date',
  'Boolean',
  'Select',
  'Multi Select',
  'Email',
  'URL',
  'Phone',
  'Currency',
  'Percentage',
  'Recipient Lookup',
]

/**
 * A workspace custom field as the builder offers it. `cf` is the
 * GET /recipients/custom-fields response row, whose `type` is the stored
 * custom_field_type value (audit F4 — the API used to send 'Multiselect').
 */
export function customFieldSpec(cf: {
  key: string
  name: string
  type: FieldType
  options?: string[]
}): RuleFieldSpec {
  const spec: RuleFieldSpec = {
    key: `customFields.${cf.key}`,
    label: cf.name,
    type: cf.type,
    group: 'custom',
  }
  if ((cf.type === 'Select' || cf.type === 'Multi Select') && cf.options) {
    spec.options = cf.options.map((o) => ({ value: o, label: o }))
  }
  return spec
}

/** The recipient_status enum, in the database's order. */
export const RECIPIENT_STATUSES = ['Active', 'Inactive', 'Bounced', 'Unsubscribed'] as const

type StatusOptionKey =
  | 'includeInactive'
  | 'excludeBouncedEmails'
  | 'excludeSuppressedRecipients'

/** Legacy operator spellings a saved or pasted rule can still carry. */
const LEGACY_OPERATORS: Readonly<Record<string, string>> = {
  equals: 'is',
  not_equals: 'is_not',
  in: 'in_list',
  not_in: 'not_in_list',
}

/**
 * Does this Status condition select recipients with `status`? Read the way the
 * engine reads it — case-insensitive, and per OPERATOR: "is not Active"
 * selects Inactive, Bounced and Unsubscribed. A blank value selects nobody
 * (the engine refuses an incomplete condition).
 */
function statusConditionSelects(
  cond: { operator?: unknown; value?: unknown },
  status: string,
): boolean {
  const op = LEGACY_OPERATORS[String(cond.operator)] ?? String(cond.operator)
  const s = status.toLowerCase()
  if (op === 'is_not_empty') return true
  if (op === 'is_empty') return false
  const values = (Array.isArray(cond.value) ? cond.value : [cond.value])
    .map((v) => (v === undefined || v === null ? '' : String(v).trim().toLowerCase()))
    .filter((v) => v !== '')
  if (values.length === 0) return false
  const v = values[0]
  switch (op) {
    case 'is':
      return s === v
    case 'is_not':
      return s !== v
    case 'in_list':
      return values.includes(s)
    case 'not_in_list':
      return !values.includes(s)
    case 'contains':
      return s.includes(v)
    case 'not_contains':
      return !s.includes(v)
    case 'starts_with':
      return s.startsWith(v)
    case 'ends_with':
      return s.endsWith(v)
    default:
      return false
  }
}

/**
 * Derive the three option toggles from the rule's Status conditions (GR9 —
 * an explicit status condition wins over the default exclusions). The
 * statuses the conditions SELECT decide:
 *   - includeInactive:             ON if a condition selects Inactive
 *   - excludeBouncedEmails:        OFF if a condition selects Bounced
 *   - excludeSuppressedRecipients: OFF if a condition selects Unsubscribed
 * The engine likewise admits anyone a status condition selects, whatever the
 * toggles say. This used to read the VALUES a condition mentions, so
 * "Status is not Active" turned nothing on and matched nobody.
 *
 * Returns overrides only when at least one status condition exists so we
 * don't clobber user-set toggles when no status rule is present.
 *
 * Case normalisation (fix ticket #117/#118 2026-04-10): both sides are
 * lower-cased, so "Status is Inactive" really turns includeInactive on.
 */
export function deriveStatusOptions(
  conditions: readonly unknown[],
): Partial<Record<StatusOptionKey, boolean>> {
  const statusConditions = conditions.filter(
    (cond): cond is { field: string; operator?: unknown; value?: unknown } =>
      !!cond &&
      typeof cond === 'object' &&
      'field' in cond &&
      (cond as { field: unknown }).field === 'status',
  )
  if (statusConditions.length === 0) return {}

  const selected = (status: string) =>
    statusConditions.some((cond) => statusConditionSelects(cond, status))

  return {
    includeInactive: selected('Inactive'),
    excludeBouncedEmails: !selected('Bounced'),
    excludeSuppressedRecipients: !selected('Unsubscribed'),
  }
}

/** Catalog categories the Lists/groups builder surfaces. */
export const BEHAVIORAL_CATEGORIES: ReadonlySet<string> = new Set([
  'phishing',
  'history',
  'training',
  'risk',
  'vishing',
  'entity',
])

// ── Vishing results (the authorised voice-call simulation module) ────────────
//
// The API catalog (apps/api …/groups/engine/vishing-rule-fields.ts) defines
// these fields — ids, operators, option VALUES — and serves them only to a
// tenant that has the Vishing module. The builder renders them from that
// catalog like every behavioral field. What lives here is their TEXT: the
// catalog's labels are English, and the builder shows them in the viewer's
// language. The API guard group-rule-vishing-results.postgres-cases.ts
// requires this file and fails when a catalog field or option has no text
// here, or this map names one the catalog lacks.

/** The catalog category, and the module a tenant needs to be offered it. */
export const VISHING_RULE_CATEGORY = 'vishing'
export const VISHING_MODULE_KEY = 'vishing'

/** Where a catalog field's words are in the message catalogues. */
export interface RuleFieldText {
  labelKey: string
  /** The unit shown beside a number box ("days", "times"). */
  unit?: { labelKey: string }
  /** Option VALUE (what is stored) → its label. */
  options?: Readonly<Record<string, { labelKey: string }>>
}

const DAYS = { labelKey: 'recipients.groupRules.vishing.units.days' }
const TIMES = { labelKey: 'recipients.groupRules.vishing.units.times' }

export const VISHING_RULE_FIELD_TEXT: Readonly<Record<string, RuleFieldText>> = {
  vishing_outcome: {
    labelKey: 'recipients.groupRules.vishing.fields.outcome',
    options: {
      Unsafe: { labelKey: 'recipients.groupRules.vishing.options.unsafe' },
      Safe: { labelKey: 'recipients.groupRules.vishing.options.safe' },
      'No Outcome': { labelKey: 'recipients.groupRules.vishing.options.noOutcome' },
    },
  },
  vishing_days_since_outcome: {
    labelKey: 'recipients.groupRules.vishing.fields.daysSinceOutcome',
    unit: DAYS,
  },
  vishing_unsafe_count: {
    labelKey: 'recipients.groupRules.vishing.fields.unsafeCount',
    unit: TIMES,
  },
  vishing_days_since_unsafe: {
    labelKey: 'recipients.groupRules.vishing.fields.daysSinceUnsafe',
    unit: DAYS,
  },
  vishing_called: {
    labelKey: 'recipients.groupRules.vishing.fields.called',
    options: {
      Called: { labelKey: 'recipients.groupRules.vishing.options.called' },
      'Never Called': { labelKey: 'recipients.groupRules.vishing.options.neverCalled' },
    },
  },
  vishing_days_since_call: {
    labelKey: 'recipients.groupRules.vishing.fields.daysSinceCall',
    unit: DAYS,
  },
  vishing_answered: {
    labelKey: 'recipients.groupRules.vishing.fields.answered',
    options: {
      Answered: { labelKey: 'recipients.groupRules.vishing.options.answered' },
      'Never Answered': { labelKey: 'recipients.groupRules.vishing.options.neverAnswered' },
    },
  },
  vishing_days_since_answer: {
    labelKey: 'recipients.groupRules.vishing.fields.daysSinceAnswer',
    unit: DAYS,
  },
}

/**
 * Whether the tenant has `module`. `enabledModules` is the tenant's module
 * set, or null while it is not known (loading, or the request failed) — and
 * "not known yet" is not "probably": only a KNOWN set that has the module
 * says yes.
 */
export function moduleIsOn(
  enabledModules: ReadonlySet<string> | null,
  module: string,
): boolean {
  return enabledModules !== null && enabledModules.has(module)
}

/**
 * The catalog fields this viewer's tenant is offered: the Vishing fields only
 * when the tenant has the Vishing module (`moduleIsOn`). The API withholds
 * them from a tenant without the module in any case; this keeps the builder
 * right while a cached catalog and the module set disagree.
 */
export function offeredRuleFields<T extends Record<string, unknown>>(
  fields: readonly T[],
  vishingOn: boolean,
): T[] {
  return fields.filter((f) => vishingOn || f.category !== VISHING_RULE_CATEGORY)
}
